import React, { useState } from "react";
import styled from "styled-components";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Card, FlexContainer, Button } from "../../styles/GlobalStyles";

const EvaluationContainer = styled(Card)`
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  position: relative;
  overflow: hidden;
`;

const EvaluationHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const EvaluationTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const EvaluationSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  opacity: 0.9;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const QuestionContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const QuestionText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const OptionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ isSelected }) =>
    isSelected ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.1)"};
  border: 1px solid
    ${({ isSelected }) =>
      isSelected ? "rgba(255, 255, 255, 0.5)" : "rgba(255, 255, 255, 0.2)"};
  color: white;
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  transition: ${({ theme }) => theme.transitions.fast};
  cursor: pointer;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const NavigationContainer = styled(FlexContainer)`
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const StepIndicator = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  opacity: 0.8;
`;

const NavButton = styled(Button).withConfig({
  shouldForwardProp: (prop) => prop !== "variant",
})<{ variant?: "primary" | "ghost" }>`
  background-color: ${({ variant }) =>
    variant === "ghost"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(255, 255, 255, 0.2)"};
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;

  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResponseArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  resize: vertical;
  transition: ${({ theme }) => theme.transitions.fast};

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

interface Question {
  id: string;
  text: string;
  type: "multiple_choice" | "text";
  options?: string[];
}

interface EvaluationCardProps {
  title: string;
  subtitle: string;
  questions: Question[];
  currentStep: number;
  totalSteps: number;
}

const mockQuestions: Question[] = [
  {
    id: "1",
    text: "A pessoa participante irá preencher a avaliação no formato que foi configurado pela pessoa administradora.",
    type: "text",
  },
  {
    id: "2",
    text: "Como você avalia seu desempenho no período?",
    type: "multiple_choice",
    options: [
      "Necessita melhora",
      "Atinge parcialmente",
      "Atinge o esperado",
      "Excede o esperado",
      "Excede muito o esperado",
    ],
  },
];

export const EvaluationCard: React.FC<EvaluationCardProps> = ({
  title = "Avaliação de Desempenho - Ciclo 1/2024",
  subtitle = "Levando em consideração o período de 27/06/2024 até 18/09/2024 como você avalia Júlia nos seguintes critérios:",
  questions = mockQuestions,
  currentStep = 4,
  totalSteps = 13,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [textResponse, setTextResponse] = useState<string>("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const currentQuestion = questions[currentQuestionIndex];

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOption("");
      setTextResponse("");
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setSelectedOption("");
      setTextResponse("");
    }
  };

  return (
    <EvaluationContainer>
      <EvaluationHeader>
        <EvaluationTitle>{title}</EvaluationTitle>
        <EvaluationSubtitle>{subtitle}</EvaluationSubtitle>
      </EvaluationHeader>

      <QuestionContainer>
        <QuestionText>{currentQuestion.text}</QuestionText>

        {currentQuestion.type === "multiple_choice" &&
        currentQuestion.options ? (
          <OptionsContainer>
            {currentQuestion.options.map((option, index) => (
              <OptionButton
                key={index}
                isSelected={selectedOption === option}
                onClick={() => setSelectedOption(option)}
              >
                {option}
              </OptionButton>
            ))}
          </OptionsContainer>
        ) : (
          <ResponseArea
            placeholder="Escreva sua resposta..."
            value={textResponse}
            onChange={(e) => setTextResponse(e.target.value)}
          />
        )}
      </QuestionContainer>

      <NavigationContainer justify="space-between">
        <NavButton
          variant="ghost"
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        >
          <ArrowLeft size={16} />
        </NavButton>

        <StepIndicator>
          {currentStep} of {totalSteps}
        </StepIndicator>

        <NavButton onClick={handleNext}>
          Next
          <ArrowRight size={16} />
        </NavButton>
      </NavigationContainer>
    </EvaluationContainer>
  );
};
